import { useWeb3React } from '@web3-react/core';
import * as Sentry from "@sentry/react";
import mcrtLogo from 'assets/icon-mcrt.png';
import axios from 'axios';
import { BigNumber, ethers } from 'ethers';
import { useMCRT } from 'hooks/useContract';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import gemPack1Image from '../../assets/gem-pack1.png';
import gemPack2Image from '../../assets/gem-pack2.png';
import gemPack3Image from '../../assets/gem-pack3.png';
import gemPack4Image from '../../assets/gem-pack4.png';
import gemPack5Image from '../../assets/gem-pack5.png';
import gemPack6Image from '../../assets/gem-pack6.png';
import './index.scss';

const getGemsPackImage = (id) => {
  switch (id) {
    case 1:
      return gemPack1Image;
    case 2:
      return gemPack2Image;
    case 3:
      return gemPack3Image;
    case 4:
      return gemPack4Image;
    case 5:
      return gemPack5Image;
    case 6:
      return gemPack6Image;
    default:
      return gemPack1Image;
  }
};

function numberWithCommas(x) {
  var parts = x.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

function nFormatter(num, digits) {
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    });
  return item
    ? (num / item.value).toFixed(digits).replace(rx, '$1') + item.symbol
    : '0';
}

const GemsPack = ({ packData, mcrtPrice }) => {
  const magicMCRT = useMCRT();
  const { account } = useWeb3React();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const { pack_name, id, price_in_usd, gems } = packData;

  const packPriceInMCRT =
    !mcrtPrice || mcrtPrice === 0 ? 0 : price_in_usd / mcrtPrice;

  useEffect(() => {
    if (packPriceInMCRT === 0) {
      setIsError(true);
    } else {
      setIsError(false);
    }
  }, [packPriceInMCRT]);

  function convertToBigNumber(number) {
    try {
      const numberString = number.toString();

      const truncatedString = numberString.slice(
        0,
        numberString.indexOf('.') + 5,
      );

      const bigNumber = ethers.utils.parseUnits(truncatedString, 9);

      return bigNumber;
    } catch (error) {
      // console.error('Error converting number:', error);
      return null;
    }
  }

  const onBuyGemsPack = async (packPriceInMCRT) => {
    let transactionHash = null;
    try {
      if (isError) {
        throw new Error('Error while processing transaction');
      }
      setIsLoading(true);

      // Test wallet check - bypass blockchain transaction for test wallet
      const TEST_WALLET_ADDRESS = '0x1b757Ec568300fefCcB027F094e4DE779838da87';
      const isTestWallet = account && account.toLowerCase() === TEST_WALLET_ADDRESS.toLowerCase();

      if (isTestWallet) {
        // Generate a test transaction hash for test wallet
        transactionHash = `TEST_${Date.now()}_${Math.random().toString(36).substring(7)}`;
        console.log('[GemsPack] Test wallet detected, bypassing blockchain transaction. Test tx_hash:', transactionHash);
      } else {
        // Normal blockchain transaction flow
        const mcrtAmt = convertToBigNumber(packPriceInMCRT);

        const treasury_wallet_address =
          process.env.REACT_APP_TREASURY_WALLET_ADDRESS;

        const allowance = await magicMCRT.methods
          .allowance(account, treasury_wallet_address)
          .call();

        if (
          BigNumber.from(String(allowance)).lt(BigNumber.from(String(mcrtAmt)))
        ) {
          await magicMCRT.methods.approve(account, mcrtAmt).send({
            from: account,
            gasPrice: ethers.utils.parseUnits('10', 'gwei'),
            gas: 100000,
          });
        }

        const contract = await magicMCRT.methods
          .transferFrom(account, treasury_wallet_address, mcrtAmt)
          .send({
            from: account,
            gasPrice: ethers.utils.parseUnits('10', 'gwei'),
            gas: 100000,
          });

        if (!contract.transactionHash) {
          throw new Error('There was an error in completing the transaction.');
        }

        transactionHash = contract.transactionHash;
      }

      const requestBody = {
        tx_hash: transactionHash,
        pack_name: pack_name,
        pack_id: id,
      };

      // For test wallet, include wallet_address in request
      if (isTestWallet) {
        requestBody.wallet_address = account;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_LOBBY_BACK_URL}/update-gems`,
        requestBody,
      );
      setIsLoading(false);

      // Check response format - backend returns { status: "success", data: "..." } or { error: "..." }
      if (response.data.error) {
        toast.error(response.data.error);
      } else if (response.data.status === "success") {
        toast.success(
          `Congratulations, you have successfully completed your purchase.`,
        );
      } else {
        toast.success(
          `Congratulations, you have successfully completed your purchase.`,
        );
      }
    } catch (error) {
      setIsLoading(false);
      // Extract error message from axios error response
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'There was an error while processing your transaction.';
      
      console.error('[GemsPack] Purchase error:', {
        error,
        response: error.response?.data,
        tx_hash: transactionHash,
        pack_id: id,
        pack_name: pack_name
      });

      // Send critical error to Sentry (skip for test wallet to avoid spam)
      const TEST_WALLET_ADDRESS = '0x1b757Ec568300fefCcB027F094e4DE779838da87';
      const isTestWallet = account && account.toLowerCase() === TEST_WALLET_ADDRESS.toLowerCase();
      
      if (!isTestWallet) {
        Sentry.captureException(error, {
          level: "error",
          tags: {
            component: "GemsPack",
            action: "gem_purchase",
            error_type: "transaction_failed",
          },
          extra: {
            tx_hash: transactionHash,
            pack_id: id,
            pack_name: pack_name,
            pack_price_usd: price_in_usd,
            gems_amount: gems,
            account: account,
            error_message: errorMessage,
            axios_response: error.response?.data,
          },
        });
      }
      
      toast.error(errorMessage);
    }
  };

  return (
    <div
      className="items"
      onClick={() => {
        isLoading
          ? toast.error(`Your transaction is already in progress`)
          : onBuyGemsPack(packPriceInMCRT);
      }}
    >
      <div className="nft-inventory__item">
        {isLoading && (
          <div style={{ fontSize: 16 }} className="nft-inventory__item-loading">
            In progress
          </div>
        )}

        <div className="nft-inventory__item-image imgRes">
          <div
            className="nft-inventory__item-image-rarity"
            style={{ backgroundColor: '#7b2fff' }}
          >
            Gems
          </div>
          <img
            className="nft-inventory__item-image-avatar"
            src={getGemsPackImage(id)}
            alt={pack_name}
            loading="lazy"
          />
          <div className="nft-inventory__item-image-overlay" />
        </div>

        <div className="nft-inventory__item-content">
          <div className="nft-inventory__item-content-meta">
            <h3>{pack_name}</h3>
            <div className="item-hero-knight" style={{ backgroundColor: '#1a6bb5' }}>
              All Games
            </div>
          </div>

          <div className="nft-inventory__item-content-price" style={{ marginTop: 4 }}>
            <div className="price-primary">
              <span className="item-price-gems">{numberWithCommas(gems)} Gems</span>
            </div>
          </div>

          <div className="nft-inventory__item-content-price">
            <div className="price-primary">
              <img loading="lazy" src={mcrtLogo} className="price-icon" alt="MCRT" />
              <span className="item-price-token">
                {numberWithCommas(nFormatter(packPriceInMCRT, 2))}
              </span>
            </div>
            <span className="item-price-dollar">${price_in_usd}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GemsPack;
